<script lang="ts">
	import StructuredData from "@components/common/StructuredData.svelte";
	import type {
		Brand as SchemaBrand,
		MerchantReturnPolicy as SchemaMerchantReturnPolicy,
		OfferShippingDetails as SchemaOfferShippingDetails,
		Organization as SchemaOrganization,
		Product as SchemaProduct,
		ProductGroup as SchemaProductGroup,
		WithContext as SchemaWithContext,
	} from "schema-dts";
	import type { Country } from "@core/schema/Country.js";
	import type { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type {
		ProductAttribute,
		ProductAttributeGroup,
		ProductAttributeGroupOfValue,
		ProductForCustomer,
		ProductVariantForCustomer,
	} from "@core/schema/Product.js";
	import { ProductAttributeGroupType } from "@core/schema/ProductAttributeGroupType.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { appName } from "@core/schema/names/appName.js";
	import { getProductDetailPath } from "@core/schema/paths/getProductDetailPath.js";
	import { productAttributeGroupTypeToStructuredDataVariesByMap } from "@core/schema/productAttributeGroupTypeToStructuredDataVariesByMap.js";
	import { productVariantTypeToStructuredDataVariesByMap } from "@core/schema/productVariantTypeToStructuredDataVariesByMap.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductAttributeGroupsOfValues } from "@core/schema/utils/getProductAttributeGroupsOfValues.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { getProductVariantAttributeCombinations } from "@core/schema/utils/getProductVariantAttributeCombinations.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { idRef, returnPolicyIdForCategory, seoFragments, seoId, shippingDetailsId } from "../../seo/seoIds.js";
	import { selectedCurrency } from "../../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../../state/selectedDestinationCountry.svelte.js";

	interface Props {
		product: ProductForCustomer;
		subcategory: SubcategoryForCustomer;
	}

	const { product, subcategory }: Props = $props();
	const { WEB_URL_OR_LOCAL, FILE_BUCKET_URL_MAP } = getGlobalSettings();
	const { tenant, countrySettings, description } = getGlobalTemplateProps();

	const productName = $derived(product.name[tenant.locale]);
	const category = $derived(subcategory.name[tenant.locale]);
	const keywords = $derived(product.metaKeywords?.[tenant.locale]);
	const brand: SchemaBrand = $derived({
		"@type": "Brand",
		name: appName,
	});
	const images = $derived(
		product.gallery
			.map((media) => (media.photo ? thumbUploadedFile(500, 0, media.photo, FILE_BUCKET_URL_MAP) : undefined))
			.filter((image) => image !== undefined),
	);

	const url = $derived(`${WEB_URL_OR_LOCAL}${getProductDetailPath(tenant.locale, product, subcategory, null, [])}`);

	function getDefaultVariantOfProduct(product: ProductForCustomer): ProductVariantForCustomer {
		const [firstVariant] = product.variants;
		if (!firstVariant) {
			throw new Error("Product has no variants.");
		}
		return firstVariant;
	}
	const defaultVariant = $derived(getDefaultVariantOfProduct(product));

	const defaultAttributes = $derived.by(() => {
		const grouped: Record<ProductAttributeGroup["id"], ProductAttribute["id"]> = {};
		for (const group of product.attributeGroups) {
			const [first] = group.values;
			if (!first) {
				continue;
			}
			grouped[group.id] = first.id;
		}
		return grouped;
	});

	const defaultProductAttributes = $derived(
		getProductAttributeGroupsOfValues(product.attributeGroups, defaultAttributes),
	);

	function getVariantUrl(variant: ProductVariantForCustomer, attributes: ProductAttributeGroupOfValue[]): string {
		return `${WEB_URL_OR_LOCAL}${getProductDetailPath(tenant.locale, product, subcategory, variant, attributes)}`;
	}

	function getSkuForVariant(variant: ProductVariantForCustomer, attributes: ProductAttributeGroupOfValue[]): string {
		const attributeIds = attributes
			.map((attribute) => attribute.value.id)
			.sort()
			.join(":");
		return attributeIds ? `${product.id}::${variant.id}::${attributeIds}` : `${product.id}::${variant.id}`;
	}

	const enabledShippingRefs = $derived(
		countrySettings
			.filter((setting) => setting.data?.delivery !== undefined)
			.map(({ country }) => idRef<SchemaOfferShippingDetails>(shippingDetailsId(WEB_URL_OR_LOCAL, country))),
	);
	const returnPolicyRef = $derived(
		idRef<SchemaMerchantReturnPolicy>(returnPolicyIdForCategory(WEB_URL_OR_LOCAL, subcategory.category)),
	);
	const sellerRef = $derived(idRef<SchemaOrganization>(seoId(WEB_URL_OR_LOCAL, seoFragments.organization)));

	function getStructuredDataForProduct(
		currency: Currency,
		destinationCountry: Country | "outsideEu",
		variant: ProductForCustomer["variants"][number],
		attributes: ProductAttributeGroupOfValue[],
		vatType: EconomicSubjectVatType,
	): SchemaWithContext<SchemaProduct> {
		const productOfVariant = getProductOfVariant(product, variant.id);
		const price = getPriceForCustomerOfProductVariant(productOfVariant, currency, destinationCountry, vatType).withTax;

		const variantSpecificFields = {
			[ProductVariantType.None]: {},
			[ProductVariantType.Size]: {
				size: variant.label[tenant.locale],
			},
			[ProductVariantType.Weight]: {
				weight: {
					"@type": "QuantitativeValue" as const,
					value: variant.weightInGrams,
					unitCode: "GRM" as const,
				},
			},
		}[product.variantType];

		// Remove eslint disable comment when ProductAttributeGroupType will have more than one type
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		const color = attributes.find((attribute) => attribute.type === ProductAttributeGroupType.Color)?.value.label[
			tenant.locale
		];

		const stock = getProductStockAmount(product.stock, productOfVariant.variant, attributes);
		const outOfStock = stock === 0;

		return {
			"@context": "https://schema.org" as const,
			"@type": "Product" as const,
			name: getProductHeadingWithAttributes(tenant.locale, {
				...product,
				attributes,
			}),
			description,
			image: images,
			url: getVariantUrl(variant, attributes),
			category,
			keywords,
			brand,
			color,
			sku: getSkuForVariant(variant, attributes),
			mpn: product.id,
			...variantSpecificFields,
			offers: {
				"@type": "Offer",
				availability: outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
				price,
				priceCurrency: currency,
				url: getVariantUrl(variant, attributes),
				seller: sellerRef,
				hasMerchantReturnPolicy: returnPolicyRef,
				shippingDetails: enabledShippingRefs,
			},
		};
	}

	const productCombinations = $derived(getProductVariantAttributeCombinations<ProductForCustomer>(product));

	const productHasMultipleVariants = $derived(productCombinations.length > 1);
	const data = $derived.by<SchemaWithContext<SchemaProduct | SchemaProductGroup> | null>(() => {
		const vatType = EconomicSubjectVatType.NotVatSubject;
		const currency = selectedCurrency.current;
		const destinationCountry = selectedDestinationCountry.current;
		if (currency === null || destinationCountry === null) {
			return null;
		}
		if (productHasMultipleVariants) {
			return {
				"@context": "https://schema.org",
				"@type": "ProductGroup",
				name: productName,
				description,
				image: images,
				url,
				category,
				keywords,
				brand,
				productGroupID: product.id,
				variesBy: [
					productVariantTypeToStructuredDataVariesByMap[product.variantType],
					...product.attributeGroups.map(({ type }) => productAttributeGroupTypeToStructuredDataVariesByMap[type]),
				].filter(Boolean),
				hasVariant: productCombinations.map(({ variant, attributes }) =>
					getStructuredDataForProduct(currency, destinationCountry, variant, attributes, vatType),
				),
			} satisfies SchemaWithContext<SchemaProductGroup>;
		}
		return getStructuredDataForProduct(currency, destinationCountry, defaultVariant, defaultProductAttributes, vatType);
	});
</script>

{#if data}
	<StructuredData {data} />
{/if}
